import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PALETTE as P } from "../tokens";
import { useAuth } from "../context/AuthContext";
import { practitionerLinkApi } from "../api/practitionerLink";
import { markPendingConsent } from "../utils/practitionerConsent";
import { Wordmark } from "../components/Wordmark";

// Flow 1's public landing page — the pre-login identity disclosure.
// Reached from an invite link (email or a practitioner-shared URL),
// unauthenticated. Shows who's asking and what they'd request BEFORE
// any login/signup happens, per the identity-before-access requirement;
// the actual accept/decline still happens on the existing consent
// dialog (ConsentDialog in ProtocolCard), reached after auth via the
// pending-consent flag — this page never asks for consent itself.
export default function InvitePage() {
  const { token: inviteToken } = useParams();
  const { user, token: authToken } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [continuing, setContinuing] = useState(false);

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const body = await practitionerLinkApi.getInvitePublic(inviteToken);
        if (!dead) setInvite(body);
      } catch {
        if (!dead) setError("This invite link isn't valid.");
      } finally {
        if (!dead) setLoading(false);
      }
    })();
    return () => { dead = true; };
  }, [inviteToken]);

  async function continueAsSelf() {
    if (continuing) return;
    setContinuing(true);
    try {
      await practitionerLinkApi.redeemInvite(inviteToken, authToken);
      markPendingConsent();
      navigate("/dashboard", { replace: true });
    } catch {
      setError("Something didn’t work. Try again.");
      setContinuing(false);
    }
  }

  function goToAuth(mode) {
    const params = new URLSearchParams({ mode, invite: inviteToken });
    if (invite?.email) params.set("email", invite.email);
    navigate(`/login?${params.toString()}`);
  }

  return (
    <div style={{
      fontFamily: "'DM Sans', sans-serif",
      background: P.bg, minHeight: "100vh", color: P.text,
      maxWidth: 420, margin: "0 auto",
      display: "flex", flexDirection: "column",
      padding: "env(safe-area-inset-top) 28px 48px",
    }}>
      <Wordmark style={{ marginTop: 40, marginBottom: 32 }} />

      {loading && (
        <p style={{ fontSize: 14, color: P.textMute }}>Loading invite…</p>
      )}

      {!loading && (error || !invite) && (
        <>
          <h1 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 26, fontStyle: "italic", fontWeight: 400,
            color: P.text, lineHeight: 1.3, marginBottom: 14,
          }}>
            This invite isn’t available.
          </h1>
          <p style={{ fontSize: 14.5, color: P.textMid, fontWeight: 300, lineHeight: 1.7 }}>
            {error || "It may have expired or already been used. Ask your practitioner for a new one."}
          </p>
        </>
      )}

      {!loading && invite && invite.status !== "pending" && (
        <>
          <h1 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 26, fontStyle: "italic", fontWeight: 400,
            color: P.text, lineHeight: 1.3, marginBottom: 14,
          }}>
            {invite.status === "expired"
              ? "This invite has expired."
              : "This invite has already been used."}
          </h1>
          <p style={{ fontSize: 14.5, color: P.textMid, fontWeight: 300, lineHeight: 1.7 }}>
            Ask {invite.practitioner_name} for a new invite, or log in if
            you’ve already set up your account.
          </p>
          <button
            type="button"
            onClick={() => navigate("/login")}
            style={{
              marginTop: 24, width: "100%", padding: 17,
              background: P.dark, border: "none", borderRadius: 16,
              cursor: "pointer", fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 18, fontStyle: "italic", color: P.bg,
            }}
          >
            Log in
          </button>
        </>
      )}

      {!loading && invite && invite.status === "pending" && (
        <>
          <div style={{ fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", color: P.primary, fontWeight: 600, marginBottom: 16 }}>
            From your practitioner
          </div>
          <h1 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 28, fontStyle: "italic", fontWeight: 400,
            color: P.text, lineHeight: 1.25, marginBottom: 14,
          }}>
            {invite.practitioner_name} has invited you to work together in Haiven.
          </h1>
          <p style={{ fontSize: 14.5, color: P.textMid, fontWeight: 300, lineHeight: 1.7, marginBottom: 8 }}>
            If you say yes, they’ll see completion data and signal trends
            only. Nothing you’ve written is shared — later you can choose
            specific entries if you want to.
          </p>
          <p style={{ fontSize: 14.5, color: P.textMid, fontWeight: 300, lineHeight: 1.7, marginBottom: 32 }}>
            Nothing is shared yet. You’ll confirm this on the next screen,
            and you can change your mind anytime.
          </p>

          {user ? (
            <button
              type="button"
              onClick={continueAsSelf}
              disabled={continuing}
              style={{
                width: "100%", padding: 17, background: P.dark, border: "none",
                borderRadius: 16, cursor: continuing ? "default" : "pointer",
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 18, fontStyle: "italic", color: P.bg,
                opacity: continuing ? 0.6 : 1,
              }}
            >
              Continue as {user.email}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => goToAuth("signup")}
                style={{
                  width: "100%", padding: 17, background: P.dark, border: "none",
                  borderRadius: 16, cursor: "pointer",
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: 18, fontStyle: "italic", color: P.bg, marginBottom: 12,
                }}
              >
                Create my account
              </button>
              <button
                type="button"
                onClick={() => goToAuth("login")}
                style={{
                  width: "100%", padding: 14, background: "none",
                  border: `1.5px solid ${P.border}`, borderRadius: 14,
                  cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                  fontSize: 13.5, color: P.textMid,
                }}
              >
                I already have an account — Log in
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
}
